
import { useCallback, useRef, useState } from 'react';

import { importSheetPredictions } from '../../../../../../core/api/external-sheets';

export interface InputDataItem {
  forecastId: string;
  organisationId: string;
  workspaceId: string;
}

/**
 * Hook that will return a function / state for syncing the master google sheets for the given inputs
 * @param inputItems - Array of input data
 */
export const useSheetsSync = () => {
  const [busy, setBusy] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, boolean>>({});

  const busyRef = useRef(busy);
  const resultsRef = useRef(results);

  const syncItem = useCallback(async (item: InputDataItem) => {
    let error = false;

    try {
      const result = await importSheetPredictions(item.workspaceId, item.organisationId, item.forecastId);
      if (result.error) {
        error = true;
      }
    } catch (e) {
      error = true;
    } finally {
      const newResults = { ...resultsRef.current };
      newResults[item.forecastId] = error ? false : true;
      resultsRef.current = newResults;
      setResults(newResults);

      const newBusy = [...busyRef.current].filter(id => id !== item.forecastId);
      busyRef.current = newBusy;
      setBusy(newBusy);
    }
  }, []);

  const syncAll = useCallback(
    (inputItems: InputDataItem[]) => {
      setBusy(inputItems.map(item => item.forecastId));
      setResults({});
      return inputItems.map(item => syncItem(item));
    },
    [syncItem]
  );

  return { busy, results, syncAll };
};

/**
 * Hook that will return a function / state for syncing the master google sheet for the given forecast ID
 * @param workspaceId - Workspace ID
 * @param organisationId - Organisation ID
 * @param inputForecastId - Forecast ID
 */
export const useSheetSync = () => {
  const [currentForecastId, setCurrentForecastId] = useState('');
  const { busy, results, syncAll } = useSheetsSync();

  const sync = useCallback(
    (workspaceId: string, organisationId: string, forecastId: string) => {
      const item: InputDataItem = { workspaceId, organisationId, forecastId };
      setCurrentForecastId(item.forecastId);
      return syncAll([item])[0];
    },
    [syncAll]
  );

  return {
    busy: busy.includes(currentForecastId),
    result: results[currentForecastId],
    sync
  };
};
